import {
  MouseEventHandler,
  PropsWithChildren,
  RefObject,
  useCallback,
  useState,
} from "react";
import React from "react";
import { useAssetReloadParametersLink } from "domains/assets/hooks/useAssetLink";
import { FileManagerVariant } from "domains/file-manager/components/FileManagerImage";
import { FmFileImage } from "domains/file-manager/interfacesV2";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import Button, { IconButton } from "domains/ui/components/Button";
import Icon from "domains/ui/components/Icon";
import { NsfwIndicator } from "domains/ui/components/NsfwIndicator";
import { useUserContext } from "domains/user/contexts/UserProvider";
import { GetAssetsByAssetIdApiResponse } from "infra/api/generated/api";

import { Box, Center, HStack, Text } from "@chakra-ui/react";

import { ActionType, FmImageCardActionsProps } from "./Actions";
import MoreButton from "./MoreButton";
import VaryButton from "./VaryButton";

export type CardContentProps<C extends object = object> = {
  file: FmFileImage;
  variant: FileManagerVariant;
  isReloadEnabled?: boolean;
  isVaryEnabled?: boolean;
  onVary?: (asset: GetAssetsByAssetIdApiResponse["asset"]) => void;
  cardWidth: number;
  containerRef: RefObject<HTMLDivElement>;
  isHovered?: boolean;
  isBlurred?: boolean;
  isPinned?: boolean;
  isWithoutActions?: boolean;
  onClick?: (file: FmFileImage) => void;
  onActionClick?: (type: ActionType) => void;
  onPinClick?: (file: FmFileImage, isPinned: boolean) => Promise<void>;

  actionsProps?: C;
  ActionsComponent?: React.FunctionComponent<FmImageCardActionsProps<C>>;
};

function CardContentWithoutMemo<C extends object = object>({
  file,
  variant,
  isReloadEnabled = false,
  isVaryEnabled = false,
  onVary,
  cardWidth,
  containerRef,
  isHovered = false,
  isBlurred = false,
  isPinned = false,
  isWithoutActions = false,
  onClick,
  onPinClick,
  onActionClick,
  actionsProps = {} as C,
  ActionsComponent,
}: CardContentProps<C>) {
  const { isReader } = useTeamContext();
  const [isPinning, setIsPinning] = useState<boolean>(false);
  const [isMoreOpen, setIsMoreOpen] = useState<boolean>(false);
  const { isUnsigned, openSignUpModal, revealAsset } = useUserContext();

  const asset = file.meta;
  const linkReloadAllParameters = useAssetReloadParametersLink(
    isHovered ? asset : undefined
  );

  const isDraft =
    variant === "skybox" && asset.metadata.type === "skybox-base-360";
  const isPinnable = !!onPinClick;
  const isReloadParamsShown =
    isReloadEnabled &&
    (linkReloadAllParameters || isUnsigned) &&
    (isHovered || isMoreOpen) &&
    !isBlurred &&
    cardWidth >= 180;

  // ----------------------------------

  const handleReloadClick = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (e) => {
      e.stopPropagation();
      if (isUnsigned) {
        e.preventDefault();
        openSignUpModal("generate");
      }
    },
    [isUnsigned, openSignUpModal]
  );

  const handlePinClick = useCallback<MouseEventHandler<HTMLButtonElement>>(
    async (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (onPinClick) {
        try {
          setIsPinning(true);
          await onPinClick(file, !isPinned);
        } finally {
          setIsPinning(false);
        }
      }
    },
    [isPinned, onPinClick, file]
  );

  const handleRevealClick = useCallback<MouseEventHandler<SVGElement>>(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      revealAsset(file.id);
    },
    [revealAsset, file.id]
  );

  const handleMoreToggle = useCallback(
    (isOpen: boolean) => setIsMoreOpen(isOpen),
    [setIsMoreOpen]
  );

  const handleMoreActionClick = useCallback(
    (action: ActionType) => {
      if (action === "more") onClick?.(file);
      onActionClick?.(action);
    },
    [onActionClick, onClick, file]
  );

  // ----------------------------------

  return (
    <Box pos="relative" w="100%" h="100%" pointerEvents="none">
      <HStack
        pos="absolute"
        top={0}
        right={0}
        minH="52px"
        p={cardWidth > 400 ? 3 : 2}
        pointerEvents="auto"
      >
        {isReloadParamsShown && (
          <Button
            size="sm"
            variant="blurred"
            internalLink={linkReloadAllParameters?.link}
            isLoading={linkReloadAllParameters?.isLoading}
            leftIcon={<Icon id="Ui/RefreshAlt" h="14px" />}
            onClick={handleReloadClick}
          >
            Recreate
          </Button>
        )}

        {isVaryEnabled && (
          <VaryButton
            isVisible={(isHovered || isMoreOpen) && !isBlurred}
            asset={asset}
            cardWidth={cardWidth}
            onVary={onVary}
          />
        )}

        {!isWithoutActions && (
          <MoreButton
            asset={asset}
            containerRef={containerRef}
            isHidden={!isHovered}
            onToggle={handleMoreToggle}
            onActionClick={handleMoreActionClick}
            actionsProps={actionsProps}
            ActionsComponent={ActionsComponent}
          />
        )}

        {isPinnable && (isPinned || isHovered || isMoreOpen) && !isReader && (
          <IconButton
            aria-label={isPinned ? "unpin image" : "pin image"}
            size="sm"
            variant="blurred"
            isLoading={isPinning}
            icon={
              <Icon
                id={isPinned ? "Ui/Pin/Solid" : "Ui/Pin/Outline"}
                h="12px"
              />
            }
            onClick={handlePinClick}
          />
        )}

        {isDraft &&
          !(isHovered || isMoreOpen) &&
          (isReloadParamsShown ||
            isPinnable ||
            !isWithoutActions ||
            isVaryEnabled) && <HighlightTag>Preview</HighlightTag>}
      </HStack>

      {isBlurred && (
        <Box
          pos="absolute"
          top={0}
          right={0}
          bottom={0}
          left={0}
          pointerEvents="auto"
        >
          <NsfwIndicator onClick={handleRevealClick} />
        </Box>
      )}

      <Box
        top={0}
        right={0}
        bottom={0}
        left={0}
        pointerEvents="none"
        {...(isMoreOpen && !isHovered ? { bgColor: "rgba(0,0,0,0.3)" } : {})}
      />
    </Box>
  );
}

export default React.memo(
  CardContentWithoutMemo
) as typeof CardContentWithoutMemo;

// ------------------------------------

const HighlightTag = React.memo(function HighlightTag({
  children,
}: PropsWithChildren) {
  return (
    <Center
      h="28px"
      px={2}
      bg="rgba(37, 37, 37, 0.35)"
      borderRadius="lg"
      pointerEvents="none"
      backdropFilter="blur(8px)"
    >
      <Text color="textPrimary" size="body.bold.sm">
        {children}
      </Text>
    </Center>
  );
});
